import _ from "lodash";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { interventionShape, populationShape, questionShape } from "../../lib/data_shapes";
import { isValidLink } from "../../lib/utils";
import ExternalLink from "../common/ExternalLink";
import { getGuidelineValues } from "../recommendation/SourceOfRecommendation";
import Box from "./Box";
import Card from "./Card";
import Certainty from "./Certainty";
import CustomTable from "./CustomTable";

const GUIDELINE_FIELDS = ["publicationDate", "latestLiteratureSearchDate", "describedAsLiving"];

const AGE_ORDER = ["infant", "child", "adolescent", "adult", "unspecified"];

const Ages = ({ ages }) => {
  return <div className="py-1 px-2 mb-2 sm:mb-0 sm:py-3 sm:px-3 block">{ages}</div>;
};

const Populations = ({ populations }) =>
  _.map(populations, (population) => (
    <div key={population["@id"]}>{_.upperFirst(population.description)}</div>
  ));

const Interventions = ({ interventions }) =>
  _.map(interventions, (intervention) => (
    <div key={intervention["@id"]}>{_.upperFirst(intervention.name)}</div>
  ));

const mapIdsToValues = (ids, values) =>
  _.chain(ids)
    .map((id) => values.find((v) => v["@id"] === id["@id"]))
    .compact()
    .value();

const getAdditionalRows = (question, t) =>
  _.chain(["evidenceProfile", "etd", "evidenceSynthesis"])
    .map((key) => {
      if (!_.isEmpty(question[`${key}Reference`])) {
        return {
          label: t(`labels.${key}`),
          value: isValidLink(question[`${key}Reference`]) ? (
            <a
              className="external-link"
              href={question[`${key}Reference`]}
              target="_blank"
              rel="noreferrer noopener"
            >
              {!_.isEmpty(question[`${key}Pages`])
                ? t("labels.see_pages", { pages: question[`${key}Pages`] })
                : question[`${key}Reference`]}
            </a>
          ) : (
            question[`${key}Reference`]
          ),
        };
      }
      return null;
    })
    .compact()
    .value();

const EmlLinks = ({ links }) => (
  <div className="gradient-links-bg py-1 px-2 sm:py-3 sm:px-3 h-full flex flex-row">
    <div className="flex-grow">
      {_.map(links, (link) => (
        <div key={link.url} className="pb-1 last:pb-0">
          <a href={link.url} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        </div>
      ))}
    </div>
    <div>
      <span className="external-logo-link eml-link" />
    </div>
  </div>
);

const QuestionData = ({
  commonStrengthAndCertainty,
  question,
  interventions,
  populations,
  evidenceTableLink,
  emlLinks,
  guideline,
}) => {
  const { t } = useTranslation();
  const questionPopulations = mapIdsToValues(question.population, populations);
  const questionInterventions = mapIdsToValues(question.intervention, interventions);

  const additionalRows = getAdditionalRows(question, t);
  const ages = _.chain(questionPopulations)
    .map("ages")
    .flatten()
    .compact()
    .uniq()
    .sort((age1, age2) => AGE_ORDER.indexOf(age1) - AGE_ORDER.indexOf(age2))
    .map((age) => t(`recommendation.ages.${age?.toLowerCase()}`))
    .join(", ")
    .value();

  const guidelineValues = useMemo(
    () =>
      _.map(getGuidelineValues(t, _.pick(guideline, GUIDELINE_FIELDS)), ({ label, value }) => {
        return {
          label,
          value: <div className="py-1 px-2 mb-2 sm:mb-0 sm:py-3 sm:px-3">{value}</div>,
        };
      }),
    [t, guideline]
  );

  return (
    <Card className={`question-data ${!commonStrengthAndCertainty ? "px-0" : ""} border`}>
      <div className="flex flex-row">
        <CustomTable
          className="flex-grow"
          valueClassName="sm:px-0 sm:py-0"
          rows={[
            {
              label: t("recommendation.population"),
              value: (
                <div className="py-1 px-2 mb-2 sm:mb-0 sm:py-3 sm:px-3">
                  <Populations populations={questionPopulations} />
                </div>
              ),
            },
            ages
              ? {
                  label: t("recommendation.age"),
                  value: <Ages ages={ages} />,
                }
              : null,
            {
              label: t("recommendation.intervention"),
              value: (
                <div className="py-1 px-2 mb-2 sm:mb-0 sm:py-3 sm:px-3">
                  <Interventions interventions={questionInterventions} />
                </div>
              ),
            },
            ...guidelineValues,
            !_.isEmpty(emlLinks)
              ? {
                  label: t("labels.eml_links"),
                  value: <EmlLinks links={emlLinks} />,
                }
              : null,

            evidenceTableLink
              ? {
                  label: t("recommendation.evidence_table_link"),
                  value: (
                    <ExternalLink
                      url={evidenceTableLink}
                      label={t("recommendation.evidence_table_link")}
                    />
                  ),
                }
              : null,
          ]}
        />
        {!commonStrengthAndCertainty && (
          <div>
            <Box
              tooltip={t("tooltips.strength")}
              className="mb-1 w-48 text-center mr-2 flex-grow-0"
              contentClassName="font-semibold"
              label={t("recommendation.recommendation_strength")}
            >
              <div>{t(`recommendation.strength.${question.gradeStrength}`)}</div>
            </Box>
            <Box
              tooltip={t("tooltips.certainty_of_evidence")}
              className="w-48 mb-1 text-center flex-grow-0"
              label={t("recommendation.certainty_of_evidence")}
            >
              <Certainty value={question.gradeCertaintyOfEvidence} />
            </Box>
          </div>
        )}
      </div>
      {!_.isEmpty(additionalRows) && (
        <div className="flex flex-row mt-8">
          <CustomTable className="flex-grow" rows={additionalRows} />
        </div>
      )}
    </Card>
  );
};

QuestionData.propTypes = {
  commonStrengthAndCertainty: PropTypes.bool,
  interventions: PropTypes.arrayOf(interventionShape),
  populations: PropTypes.arrayOf(populationShape),
  questions: PropTypes.arrayOf(questionShape),
  emlLinks: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      label: PropTypes.string,
    })
  ),
};

QuestionData.defaultProps = {
  commonStrengthAndCertainty: true,
  emlLinks: [],
  interventions: [],
  populations: [],
  questions: [],
};

export default QuestionData;
